import React, { useState, useEffect, useCallback } from 'react';
import workRequestsApi, { WorkRequest } from '../../api/workRequestsApi';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Icons } from '../../components/common/Icons';
import { formatDateTime } from '../../utils/helpers';
import './WorkRequests.css';

interface SimpleProject {
  id: number;
  name: string;
  project_type: string;
  client_name?: string;
}

export function WorkRequests() {
  const { user } = useAuth();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const canSubmit = hasPermission('requests.submit_work');
  const canView = hasPermission('requests.view_work');
  const canResolve = hasPermission('requests.resolve_work');

  const [activeTab, setActiveTab] = useState<'my' | 'pending' | 'resolved'>(canView ? 'pending' : 'my');
  const [myRequests, setMyRequests] = useState<WorkRequest[]>([]);
  const [pendingRequests, setPendingRequests] = useState<WorkRequest[]>([]);
  const [resolvedRequests, setResolvedRequests] = useState<WorkRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createMessage, setCreateMessage] = useState('');
  const [creating, setCreating] = useState(false);

  // Resolve modal
  const [resolveTarget, setResolveTarget] = useState<WorkRequest | null>(null);
  const [projects, setProjects] = useState<SimpleProject[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [projectSearch, setProjectSearch] = useState('');
  const [resolving, setResolving] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [employeeAssignedIds, setEmployeeAssignedIds] = useState<Set<number>>(new Set());

  const [processing, setProcessing] = useState<Set<number>>(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const promises: Promise<any>[] = [];

      if (canSubmit) {
        promises.push(workRequestsApi.getMyRequests());
      } else {
        promises.push(Promise.resolve([]));
      }

      if (canView) {
        promises.push(workRequestsApi.getPendingRequests('pending'));
        promises.push(workRequestsApi.getPendingRequests('resolved'));
      } else {
        promises.push(Promise.resolve([]));
        promises.push(Promise.resolve([]));
      }

      const [my, pending, resolved] = await Promise.all(promises);
      setMyRequests(my);
      setPendingRequests(pending);
      setResolvedRequests(resolved);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load work requests');
    } finally {
      setLoading(false);
    }
  }, [canSubmit, canView]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await workRequestsApi.createWorkRequest(createMessage || undefined);
      setShowCreateModal(false);
      setCreateMessage('');
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to create work request');
    } finally {
      setCreating(false);
    }
  };

  const handleCancel = async (id: number) => {
    setProcessing(prev => new Set(prev).add(id));
    try {
      await workRequestsApi.cancelWorkRequest(id);
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to cancel');
    } finally {
      setProcessing(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const openResolveModal = async (wr: WorkRequest) => {
    setResolveTarget(wr);
    setSelectedProjectId(null);
    setProjectSearch('');
    setEmployeeAssignedIds(new Set());
    setLoadingProjects(true);
    try {
      const res = await workRequestsApi.getEmployeeAssignments(wr.id);
      setProjects(res.projects.map(p => ({
        id: p.id,
        name: p.name,
        project_type: p.project_type || '',
        client_name: p.client_name || '',
      })));
      setEmployeeAssignedIds(new Set(res.assigned_project_ids));
    } catch {
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleResolve = async () => {
    if (!resolveTarget || !selectedProjectId) return;
    setResolving(true);
    try {
      await workRequestsApi.resolveWorkRequest(resolveTarget.id, selectedProjectId);
      setResolveTarget(null);
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to resolve');
    } finally {
      setResolving(false);
    }
  };

  // All timestamps are UTC. Shift by +5:30 to get IST dates so the cutoff
  // follows IST midnight, matching the backend guard.
  const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
  const toIstDate = (utcIso: string) =>
    new Date(new Date(utcIso).getTime() + IST_OFFSET_MS).toISOString().split('T')[0];
  const todayIst = new Date(Date.now() + IST_OFFSET_MS).toISOString().split('T')[0];
  const hasSubmittedToday = myRequests.some(r => !!r.created_at && r.status !== 'cancelled' && toIstDate(r.created_at) === todayIst);

  const filteredProjects = projects.filter(p =>
    p.name.toLowerCase().includes(projectSearch.toLowerCase()) ||
    (p.client_name || '').toLowerCase().includes(projectSearch.toLowerCase())
  );

  const selectedIsAssigned = selectedProjectId ? employeeAssignedIds.has(selectedProjectId) : false;

  const statusBadge = (status: string) => {
    const cls = status === 'pending' ? 'badge-pending' : status === 'resolved' ? 'badge-resolved' : 'badge-cancelled';
    return <span className={`wr-status-badge ${cls}`}>{status}</span>;
  };

  if (error) {
    return (
      <div className="wr-page">
        <div className="wr-error">
          <Icons.AlertCircle />
          <h2>Error</h2>
          <p>{error}</p>
          <Button onClick={fetchData}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="wr-page">
      <header className="wr-header">
        <div>
          <h1>Work Requests</h1>
          <p>Request work when you're available, or assign projects to idle team members.</p>
        </div>
        {canSubmit && (
          <Button onClick={() => setShowCreateModal(true)} disabled={hasSubmittedToday}>
            <Icons.Plus /> {hasSubmittedToday ? 'Request Submitted Today' : 'New Work Request'}
          </Button>
        )}
      </header>

      {/* Tabs */}
      <div className="wr-tabs">
        {canSubmit && (
          <button
            className={`wr-tab ${activeTab === 'my' ? 'active' : ''}`}
            onClick={() => setActiveTab('my')}
          >
            My Requests
            {myRequests.filter(r => r.status === 'pending').length > 0 && (
              <span className="wr-tab-count">{myRequests.filter(r => r.status === 'pending').length}</span>
            )}
          </button>
        )}
        {canView && (
          <>
            <button
              className={`wr-tab ${activeTab === 'pending' ? 'active' : ''}`}
              onClick={() => setActiveTab('pending')}
            >
              Pending Requests
              {pendingRequests.length > 0 && (
                <span className="wr-tab-count">{pendingRequests.length}</span>
              )}
            </button>
            <button
              className={`wr-tab ${activeTab === 'resolved' ? 'active' : ''}`}
              onClick={() => setActiveTab('resolved')}
            >
              Approved
              {resolvedRequests.length > 0 && (
                <span className="wr-tab-count wr-tab-count-resolved">{resolvedRequests.length}</span>
              )}
            </button>
          </>
        )}
      </div>

      {loading ? (
        <div className="wr-loading"><div className="wr-spinner" /></div>
      ) : (
        <>
          {/* ── My Requests Tab ── */}
          {activeTab === 'my' && (
            <div className="wr-list">
              {myRequests.length === 0 ? (
                <Card className="wr-empty-card">
                  <div className="wr-empty">
                    <Icons.FileText />
                    <h3>No work requests yet</h3>
                    <p>When you're available and need a project assignment, create a work request.</p>
                  </div>
                </Card>
              ) : (
                myRequests.map(wr => (
                  <Card key={wr.id} className="wr-card">
                    <div className="wr-card-row">
                      <div className="wr-card-main">
                        <div className="wr-card-top">
                          {statusBadge(wr.status)}
                          <span className="wr-card-time">{formatDateTime(wr.created_at)}</span>
                        </div>
                        {wr.message && <p className="wr-card-message">{wr.message}</p>}
                        {wr.status === 'resolved' && (
                          <p className="wr-card-resolved">
                            Assigned to <strong>{wr.resolved_project_name}</strong> by {wr.resolver_name}
                          </p>
                        )}
                      </div>
                      {wr.status === 'pending' && (
                        <Button
                          variant="secondary"
                          onClick={() => handleCancel(wr.id)}
                          disabled={processing.has(wr.id)}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </Card>
                ))
              )}
            </div>
          )}

          {/* ── Pending Requests Tab ── */}
          {activeTab === 'pending' && (
            <div className="wr-list">
              {pendingRequests.length === 0 ? (
                <Card className="wr-empty-card">
                  <div className="wr-empty">
                    <Icons.CheckCircle />
                    <h3>No pending work requests</h3>
                    <p>All team members are currently assigned to projects.</p>
                  </div>
                </Card>
              ) : (
                pendingRequests.map(wr => (
                  <Card key={wr.id} className="wr-card wr-card-pending">
                    <div className="wr-card-row">
                      <div className="wr-card-avatar">
                        {wr.employee_name?.charAt(0).toUpperCase() || '?'}
                      </div>
                      <div className="wr-card-main">
                        <div className="wr-card-top">
                          <span className="wr-card-name">{wr.employee_name}</span>
                          <span className="wr-card-dept">{wr.employee_department || ''}</span>
                          <span className="wr-card-time">{formatDateTime(wr.created_at)}</span>
                        </div>
                        {wr.message && <p className="wr-card-message">{wr.message}</p>}
                        {!wr.message && <p className="wr-card-message wr-muted">Available for project assignment</p>}
                      </div>
                      {canResolve && (
                        <Button onClick={() => openResolveModal(wr)}>
                          <Icons.Folder /> Assign Project
                        </Button>
                      )}
                    </div>
                  </Card>
                ))
              )}
            </div>
          )}

          {/* ── Approved (Resolved) Requests Tab ── */}
          {activeTab === 'resolved' && (
            <div className="wr-list">
              {resolvedRequests.length === 0 ? (
                <Card className="wr-empty-card">
                  <div className="wr-empty">
                    <Icons.CheckCircle />
                    <h3>No approved requests</h3>
                    <p>Approved work requests will appear here.</p>
                  </div>
                </Card>
              ) : (
                resolvedRequests.map(wr => (
                  <Card key={wr.id} className="wr-card wr-card-resolved">
                    <div className="wr-card-row wr-card-row-resolved">
                      <div className="wr-card-avatar wr-avatar-resolved">
                        {wr.employee_name?.charAt(0).toUpperCase() || '?'}
                      </div>
                      <div className="wr-card-main">
                        <div className="wr-card-top">
                          <span className="wr-card-name">{wr.employee_name}</span>
                          <span className="wr-card-dept">{wr.employee_department || ''}</span>
                        </div>
                        {wr.message && <p className="wr-card-message">{wr.message}</p>}
                        <p className="wr-card-resolved">
                          <Icons.Check /> Assigned to <strong>{wr.resolved_project_name}</strong> by {wr.resolver_name}
                        </p>
                      </div>
                      <div className="wr-card-right">
                        <span className="wr-card-time">{formatDateTime(wr.resolved_at || wr.created_at)}</span>
                        <span className="wr-status-badge badge-resolved">Approved</span>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          )}

        </>
      )}

      {/* ── Create Modal ── */}
      {showCreateModal && (
        <div className="wr-modal-overlay" onClick={() => !creating && setShowCreateModal(false)}>
          <div className="wr-modal" onClick={e => e.stopPropagation()}>
            <div className="wr-modal-header">
              <h2>New Work Request</h2>
              <button className="wr-modal-close" onClick={() => setShowCreateModal(false)}><Icons.X /></button>
            </div>
            <div className="wr-modal-body">
              <p className="wr-modal-desc">
                Let your manager and PMO know you're available for work.
              </p>
              <label className="wr-label">Message (optional)</label>
              <textarea
                className="wr-textarea"
                placeholder="e.g., Available from today, open to any project type..."
                value={createMessage}
                onChange={e => setCreateMessage(e.target.value)}
                maxLength={120}
                rows={3}
              />
              <div className="wr-char-count">{createMessage.length}/120</div>
            </div>
            <div className="wr-modal-footer">
              <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={creating}>Cancel</Button>
              <Button onClick={handleCreate} disabled={creating}>
                {creating ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Resolve Modal ── */}
      {resolveTarget && (
        <div className="wr-modal-overlay" onClick={() => !resolving && setResolveTarget(null)}>
          <div className="wr-modal wr-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="wr-modal-header">
              <h2>Assign Project to {resolveTarget.employee_name}</h2>
              <button className="wr-modal-close" onClick={() => setResolveTarget(null)}><Icons.X /></button>
            </div>
            <div className="wr-modal-body">
              <p className="wr-modal-desc">
                Select a project to assign. This will resolve the work request and the employee can start filling timesheets.
              </p>
              <input
                type="text"
                className="wr-search-input"
                placeholder="Search projects..."
                value={projectSearch}
                onChange={e => setProjectSearch(e.target.value)}
              />
              <div className="wr-project-list">
                {loadingProjects ? (
                  <div className="wr-loading-sm"><div className="wr-spinner" /></div>
                ) : filteredProjects.length === 0 ? (
                  <p className="wr-no-results">No active projects found</p>
                ) : (
                  filteredProjects.map(p => (
                    <div
                      key={p.id}
                      className={`wr-project-option ${selectedProjectId === p.id ? 'selected' : ''}`}
                      onClick={() => setSelectedProjectId(p.id)}
                    >
                      <div className="wr-project-option-info">
                        <span className="wr-project-option-name">
                          {p.name}
                          {employeeAssignedIds.has(p.id) && (
                            <span className="wr-assigned-tag">Assigned</span>
                          )}
                        </span>
                        <span className="wr-project-option-meta">
                          {p.project_type && <span className="wr-project-type-tag">{p.project_type.toUpperCase()}</span>}
                          {p.client_name && <span>{p.client_name}</span>}
                        </span>
                      </div>
                      {selectedProjectId === p.id && <Icons.Check />}
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="wr-modal-footer">
              <Button variant="secondary" onClick={() => setResolveTarget(null)} disabled={resolving}>Cancel</Button>
              <Button onClick={handleResolve} disabled={resolving || !selectedProjectId}>
                {resolving ? 'Resolving...' : selectedIsAssigned ? 'Resolve' : 'Assign & Resolve'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WorkRequests;
